import classnames from "classnames";
import PropTypes from "prop-types";
import React from "react";
// eslint-disable-next-line css-modules/no-unused-class
import styles from "./button.css";

type ButtonType = "cta" | "primary" | "secondary" | "tertiary" | "temp-grey";

const STYLE_TYPE_CLASSES: { readonly [bt in ButtonType]: string } = {
    cta: styles.cta,
    primary: styles.primary,
    secondary: styles.secondary,
    tertiary: styles.tertiary,
    "temp-grey": styles.tempGrey,
};

function disabledCB(event: React.MouseEvent): void {
    event.preventDefault();
}

type Renderable<P> = React.ComponentType<P> | keyof React.ReactDOM;

interface ButtonProps<P> {
    className: string;
    href?: string;
    element?: Renderable<P>;
    fullHeight?: boolean;
    fullWidth?: boolean;
    openInNewTab?: boolean;
    disabled?: boolean;
    styleType: ButtonType;
}

// Pretty much everything's going to be a button or a link so let's not get bogged down
type DefaultElementProps = JSX.IntrinsicElements["a"] & JSX.IntrinsicElements["button"];

/** BUTTONS FOR THE BUTTON GOD */
export function Button<ElementProps = DefaultElementProps>({
    className,
    element,
    fullHeight,
    fullWidth,
    openInNewTab,
    styleType,
    ...props
}: ButtonProps<ElementProps> & ElementProps): JSX.Element {
    let Component: Renderable<ElementProps> = props.href ? "a" : "button";
    if (element) {
        Component = element;
    }
    const typeClass = STYLE_TYPE_CLASSES[styleType];
    if (!typeClass) {
        throw new Error(`Unknown type ${styleType}!`);
    }

    let elementProps = props as DefaultElementProps;

    elementProps.className = classnames(className, styles.button, typeClass, {
        [styles.fullWidth]: fullWidth,
        [styles.fullHeight]: fullHeight,
        [styles.disabled]: props.disabled,
        [styles.buttonButton]: !props.href,
    });

    if (props.href) {
        elementProps.target = openInNewTab ? "_blank" : "_self";
    }
    if (props.disabled) {
        elementProps.onClick = disabledCB;
    }

    return <Component {...(elementProps as any)} />;
}

export default Button;

Button.propTypes = {
    /** What goes in the button */
    children: PropTypes.node.isRequired,
    /** classname */
    className: PropTypes.string,
    /** Allows you to override what HTML element the button renders as */
    element: PropTypes.oneOf(["a", "button", "span"]),
    /** Prevent button being clickable (& visual state) */
    disabled: PropTypes.bool,
    /** Button grows horizontally to fit its container */
    fullWidth: PropTypes.bool,
    /** Button grows vertically to fit its container */
    fullHeight: PropTypes.bool,
    /** If this button should be a link, set this to where it should go */
    href: PropTypes.string,
    /** Event handler */
    onClick: PropTypes.func,
    /** If this button is a link, open in a _blank window */
    openInNewTab: PropTypes.bool,
    /** What style this button should show as */
    styleType: PropTypes.oneOf(["cta", "primary", "secondary", "tertiary", "temp-grey"]).isRequired,
};
